import { SearchIcon } from '@chakra-ui/icons';
import {
    Box,
    Input,
    InputGroup,
    InputLeftElement,
    Select,
    Spinner,
    Text,
    useColorModeValue,
    VStack
} from '@chakra-ui/react';
import FormModal from '../../../components/FormModal';
import React, { useMemo, useState } from 'react';
import { translate } from '../../../i18n';
import { GameDto } from '../../../models/game/dto/game.dto';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { classifyRead, scopePromptI18nKey } from '../../_shared/readState';
import { useGetUsers } from '../../user-management/hooks/useGetUsers';
import { useAdminAddUserToGame } from '../hooks/useAdminAddUserToGame';

interface AddUserToGameModalProps {
    isOpen: boolean;
    onClose: () => void;
    game: GameDto;
}

export const AddUserToGameModal: React.FC<AddUserToGameModalProps> = ({
    isOpen,
    onClose,
    game,
}) => {
    const [selectedUserId, setSelectedUserId] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState('');
    const { selectedOrganization } = useSelectedOrganization();
    // Gated on `isOpen`: this modal is mounted next to GameDetailsPage rather
    // than on open, so without the gate every org manager who viewed ANY match
    // downloaded the entire organization roster for a dialog they had not
    // opened (Sentry DASHBOARD-Z). The `isOrgManager` gate one level up fixed
    // the coach *disclosure*; it left the payload.
    // R92-5 — `isError` is taken as well as `isLoading`. Post-R89-1 a refused
    // `GET /users` leaves `data === undefined`, `filteredUsers` falls to `[]`,
    // and this modal told an operator who had just opened it to add a player
    // that there are "no users found" in their organization. The roster is a
    // tenant-scoped admin read, so a stale token or an org the operator is no
    // longer a member of is refused on top of the ordinary 5xx/network paths.
    // R114-1 — the read has FOUR states and this dialog rendered three of them
    // as one. `useGetUsers` is gated `enabled: Boolean(organizationId) &&
    // isOpen`, and `organizationId` is `selectedOrganization?.id` from a zustand
    // store that starts `undefined` and is filled by `GET my-organizations`. So
    // an operator who opens this page from a link or a bookmark before that read
    // resolves — or at all, if it fails or comes back empty, which leaves the
    // whole dashboard with nothing selected for the session (run 100) — left the
    // query DISABLED. React Query v4 reports a disabled query as
    // `isLoading: true`, so `usersLoading` drove the spinner below forever: an
    // open "Add player" dialog with a working search box above a spinner that
    // resolves for nobody. Nothing on this dialog can fix it; the control that
    // can is the organization picker in the navbar, which is what the
    // never-issued branch now says.
    //
    // Note the button that opens this dialog still renders in that state:
    // `useIsOrgManager` falls back to the global `role` claim when no org is
    // selected and that fallback is ADMIN (deliberately fail-open, see
    // `resolveUserRole`), so nothing upstream hides the door.
    const { data: users, isInitialLoading: usersInitialLoading, isError: isUsersError } = useGetUsers(
        selectedOrganization?.id,
        undefined,
        { enabled: isOpen },
    );
    const usersReadState = classifyRead(isUsersError, usersInitialLoading, users);
    const addUserMutation = useAdminAddUserToGame();

    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

    // Filter users based on search term and exclude current participants
    const filteredUsers = useMemo(() => {
        if (!users) return [];

        const participantIds = new Set(game.participants.map(p => p.participant.id));

        return users.filter(user => {
            // Exclude users who are already participants
            if (participantIds.has(user.id)) return false;

            // Filter by search term
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
                const email = user.email?.toLowerCase() || '';
                return fullName.includes(searchLower) || email.includes(searchLower);
            }

            return true;
        });
    }, [users, game.participants, searchTerm]);

    const handleAddUser = async () => {
        if (!selectedUserId) return;

        try {
            await addUserMutation.mutateAsync({
                gameId: game.id,
                userId: selectedUserId,
            });

            // Reset form and close modal
            setSelectedUserId('');
            setSearchTerm('');
            onClose();
        } catch (error) {
            // Error is handled by the mutation's onError callback
        }
    };

    const handleClose = () => {
        setSelectedUserId('');
        setSearchTerm('');
        onClose();
    };

    const selectedUser = users?.find(user => user.id === selectedUserId);

    return (
        <FormModal
            isOpen={isOpen}
            onClose={handleClose}
            size="md"
            title={translate('game.add_user_to_game')}
            onSubmit={handleAddUser}
            submitLabel={translate('game.add_user')}
            cancelLabel={translate('game.cancel_edit')}
            isSubmitting={addUserMutation.isLoading}
            isSubmitDisabled={!selectedUserId || addUserMutation.isLoading}
        >
                    <VStack spacing={4} align="stretch">
                        <Text fontSize="sm" color="gray.600">
                            {translate('game.add_user_to_game_description')}
                        </Text>

                        <Box>
                            <Text fontWeight="bold" mb={2}>
                                {translate('game.select_user')}
                            </Text>

                            <InputGroup mb={3}>
                                <InputLeftElement pointerEvents='none'>
                                    <SearchIcon color='gray.400' />
                                </InputLeftElement>
                                <Input
                                    placeholder={translate('game.search_users')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    borderColor={borderColor}
                                />
                            </InputGroup>

                            {usersReadState === 'loading' ? (
                                <Box display="flex" justifyContent="center" p={4}>
                                    <Spinner />
                                </Box>
                            ) : usersReadState === 'never-issued' ? (
                                /**
                                 * Nobody asked. A disabled Select carrying the
                                 * BORROWED scope key rather than a full
                                 * `ScopePrompt` panel — this is one control
                                 * inside a form, and the sibling picker one
                                 * feature over (`UserMembershipTypePicker`,
                                 * run 109) settled the same question the same
                                 * way. `scopePromptI18nKey` owns the choice so
                                 * this and `ScopePrompt` cannot drift (D59).
                                 */
                                <Select
                                    placeholder={translate(scopePromptI18nKey('organization') as any)}
                                    isDisabled
                                />
                            ) : usersReadState === 'unreadable' ? (
                                <Text color="danger.500" fontSize="sm" py={2}>
                                    {translate('user.error_loading')}
                                </Text>
                            ) : (
                                <Select
                                    placeholder={translate('game.select_user_placeholder')}
                                    value={selectedUserId}
                                    onChange={(e) => setSelectedUserId(e.target.value)}
                                    borderColor={borderColor}
                                    maxH="200px"
                                    overflowY="auto"
                                >
                                    {filteredUsers.length === 0 ? (
                                        <option disabled>{translate('game.no_users_found')}</option>
                                    ) : (
                                        filteredUsers.map((user) => (
                                            <option key={user.id} value={user.id}>
                                                {user.firstName} {user.lastName} ({user.email})
                                            </option>
                                        ))
                                    )}
                                </Select>
                            )}
                        </Box>

                        {selectedUser && (
                            <Box p={3} bg="gray.50" borderRadius="md">
                                <Text fontWeight="bold" fontSize="sm" mb={1}>
                                    {translate('game.selected_user')}:
                                </Text>
                                <Text fontSize="sm">
                                    {selectedUser.firstName} {selectedUser.lastName}
                                </Text>
                                <Text fontSize="sm" color="gray.600">
                                    {selectedUser.email}
                                </Text>
                                {selectedUser.level && (
                                    <Text fontSize="sm" color="gray.600">
                                        {translate('game.level')}: {selectedUser.level}
                                    </Text>
                                )}
                            </Box>
                        )}
                    </VStack>
        </FormModal>
    );
};
